import React, { useRef } from 'react';
import { motion } from 'motion/react';
import { useSiteContent } from '../../hooks/useSiteContent';

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const PlayStoreIcon = () => (
  <svg viewBox="0 0 512 512" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.7c15.4-8.9 21.2-22.8 21.2-34.7 0-11.9-5.8-25.8-22.4-35.5zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
  </svg>
);

const PhoneFrame = ({ isMobile = false }: { isMobile?: boolean }) => {
  // Smartphone Proportions (approx 9:19.5)
  const width = isMobile ? 260 : 340;
  const height = width * (19.5 / 9);

  return (
    <div 
      style={{ width: `${width}px`, height: `${height}px` }}
      className="relative bg-zinc-950 rounded-[3.2rem] border-[12px] border-[#1a1a1a] shadow-[0_40px_100px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col"
    >
      {/* Dynamic Island */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-b-2xl z-50"></div>
      
      {/* Screen Content */}
      <div className="flex-grow flex flex-col p-8 pt-12 overflow-hidden bg-black">
         <div className="flex justify-between items-center mb-10">
            <img 
              src="https://realta.b-cdn.net/Media/Realta-Icon%20Logo%202025%20Blue%20Blue.svg" 
              alt="Realta" 
              width="40"
              height="40"
              loading="lazy"
              decoding="async"
              className="h-10 w-auto object-contain drop-shadow-[0_0_10px_rgba(54,180,253,0.3)]" 
            />
            <div className="w-10 h-10 rounded-full bg-zinc-900 border border-white/5"></div>
         </div>
         
         <div className="space-y-6">
            <div className="h-32 bg-zinc-900/60 rounded-2xl border border-white/5 p-6 flex flex-col justify-end">
               <div className="h-2 w-24 bg-brand-500 mb-3 rounded-full"></div>
               <div className="h-4 w-40 bg-white/5 rounded-full"></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
               <div className="aspect-square bg-zinc-900/60 rounded-2xl border border-white/5"></div>
               <div className="aspect-square bg-zinc-900/60 rounded-2xl border border-white/5"></div>
            </div>
            <div className="h-16 bg-brand-500 rounded-2xl flex items-center justify-center shadow-lg">
               <div className="h-1.5 w-32 bg-black/20 rounded-full"></div>
            </div>
         </div>
      </div>
      
      {/* Tab Bar Mock */}
      <div className="h-24 bg-zinc-900/40 backdrop-blur-md border-t border-white/5 flex items-center justify-around px-4">
         {[1, 2, 3, 4].map(i => (
           <div key={i} className={`w-10 h-1 rounded-full ${i === 1 ? 'bg-brand-500' : 'bg-white/5'}`}></div>
         ))}
      </div>
    </div>
  );
};

export const AppDownload: React.FC<{ content?: any }> = ({ content: propContent }) => {
  const { content: homeContent } = useSiteContent('home');
  const content = propContent || homeContent?.appDownload;
  const containerRef = useRef<HTMLElement>(null);
  
  const title = content?.title || "Download Our";
  const titleAccent = content?.titleAccent || "Mobile App";
  const description = content?.description || "Instant bookings, media downloads and playback on demand, take control of your marketing from anywhere.";
  const appStoreUrl = content?.appStoreUrl || "https://apps.apple.com/ca/app/realta-media-agency/id6757725284";
  const playStoreUrl = content?.playStoreUrl || "#";

  return (
    <section 
      ref={containerRef}
      className="relative bg-black pt-12 md:pt-20 pb-0 overflow-hidden border-t border-white/5"
    >
      <div className="max-w-[1920px] mx-auto px-6 md:px-12">
        
        {/* MOBILE VIEW - Stationary, No Animation */}
        <div className="flex md:hidden flex-col items-center relative z-10 pb-20">
          
          {/* Label on Top */}
          <div className="flex items-center gap-3 mb-10">
            <span className="w-8 h-[1px] bg-brand-500/40"></span>
            <span className="text-brand-500 font-display font-bold uppercase tracking-mega text-[10px]">Mobile Experience</span>
          </div>

          {/* Phone (Stationary, Half visible with Glow) */}
          <div className="relative h-[280px] w-full flex justify-center overflow-visible mb-[-20px] z-0">
             {/* Glow Behind Phone */}
             <div className="absolute inset-0 flex items-center justify-center translate-y-20 pointer-events-none">
                <div className="w-[300px] h-[300px] bg-brand-500/15 blur-[100px] rounded-full"></div>
             </div>

             <div className="relative z-0 origin-bottom">
               <PhoneFrame isMobile />
             </div>

             {/* Soft Merge Gradient */}
             <div className="absolute inset-0 top-[60%] bottom-0 bg-gradient-to-b from-transparent via-black to-black z-10 pointer-events-none"></div>
          </div>

          {/* Background overlay for content visibility */}
          <div className="relative z-20 w-full bg-black text-center -mt-10 pt-0">
            <h2 className="text-4xl font-display font-bold uppercase text-white mb-6 tracking-tighter leading-tight">
              {title} <br/> <span className="text-outline-brand">{titleAccent}</span>
            </h2>
            <p className="text-zinc-500 text-sm font-light leading-relaxed mb-12 max-w-sm mx-auto px-4">
              {description}
            </p>
            
            <div className="flex flex-col gap-4 w-full px-4 max-w-md mx-auto">
              <a 
                href={appStoreUrl} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-4 bg-white text-black h-16 w-full rounded-sm hover:bg-brand-500 transition-all active:scale-95 shadow-lg"
                aria-label="Download on the App Store"
              >
                <AppleIcon />
                <div className="flex flex-col items-start leading-none text-left">
                  <span className="text-[10px] uppercase font-bold tracking-widest opacity-40">Download on the</span>
                  <span className="text-xs font-display font-bold uppercase tracking-widest mt-1">App Store</span>
                </div>
              </a>
              
              <a 
                href={playStoreUrl} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-4 bg-zinc-900 border border-white/5 text-white h-16 w-full rounded-sm hover:border-brand-500 transition-all active:scale-95 shadow-lg"
                aria-label="Download on Google Play"
              >
                <PlayStoreIcon />
                <div className="flex flex-col items-start leading-none text-left">
                  <span className="text-[10px] uppercase font-bold tracking-widest opacity-40">Android</span>
                  <span className="text-xs font-display font-bold uppercase tracking-widest mt-1">Play Store</span>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* DESKTOP VIEW - Stationary, reduced height, half-cut phone, symmetric alignment */}
        <div className="hidden md:flex flex-col lg:flex-row items-center justify-between min-h-[450px] lg:h-[500px] relative py-12 lg:py-0">
          
          {/* Left Side: Symmetric Content Alignment */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center items-center lg:items-start text-center lg:text-left z-20 mb-12 lg:mb-0">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-[1px] bg-brand-500/40"></span>
              <span className="text-brand-500 font-display font-bold uppercase tracking-mega text-[11px]">Mobile Experience</span>
            </div>

            <h2 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold uppercase text-white mb-6 tracking-tighter leading-[0.85]">
              {title} <br/>
              <span className="text-outline-brand">{titleAccent}</span>
            </h2>

            <p className="text-zinc-500 text-lg md:text-xl font-light leading-relaxed mb-8 max-w-xl">
              {description}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-4 w-full sm:w-auto">
              <a 
                href={appStoreUrl} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-4 bg-white text-black h-14 px-8 rounded-sm hover:bg-brand-500 hover:text-white transition-all shadow-2xl active:scale-95 whitespace-nowrap min-w-[200px]"
                aria-label="Download on the App Store"
              >
                <AppleIcon />
                <div className="flex flex-col items-start leading-none text-left">
                  <span className="text-[10px] uppercase font-bold tracking-widest opacity-40">Download on the</span>
                  <span className="text-sm font-display font-bold uppercase tracking-widest mt-1">App Store</span>
                </div>
              </a>
              
              <a 
                href={playStoreUrl} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-4 bg-zinc-900 border border-white/5 text-white h-14 px-8 rounded-sm hover:border-brand-500 transition-all shadow-2xl active:scale-95 whitespace-nowrap min-w-[200px]"
                aria-label="Download on Google Play"
              >
                <PlayStoreIcon />
                <div className="flex flex-col items-start leading-none text-left">
                  <span className="text-[10px] uppercase font-bold tracking-widest opacity-40">Download on the</span>
                  <span className="text-sm font-display font-bold uppercase tracking-widest mt-1">Play Store</span>
                </div>
              </a>
            </div>
          </div>

          {/* Right Side: Stationary Half-Cut Phone */}
          <div className="flex w-full lg:w-1/2 relative h-[300px] lg:h-full items-start justify-center overflow-hidden">
            {/* Centered Ambient Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[110%] h-[110%] bg-brand-500/5 blur-[160px] rounded-full pointer-events-none"></div>
            
            {/* The Phone: Stationary and positioned so bottom half is naturally outside the container */}
            <div className="relative z-10 translate-y-10 lg:translate-y-20">
              <PhoneFrame />
            </div>

            {/* Bottom edge fade-to-black for a polished section transition */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black via-black/80 to-transparent z-20 pointer-events-none" />
          </div>
        </div>

      </div>
    </section>
  );
};